"use client";

import { useMutation } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { showToast } from "@/components/ui/sonner";
import { register } from "@/lib/api/generated";
import type { RegisterRequest } from "@/lib/api/model";
import { getStoredAttribution } from "@/lib/attribution";
import { currentCometToken } from "@/lib/cometly";
import {
  appendCurrentInvitationToken,
  buildVerifyEmailPath,
} from "@/lib/invitation-links";
import { useFingerprintEventId } from "./use-fingerprint-event-id";

export function useRegister() {
  const router = useRouter();
  const getFingerprintEventId = useFingerprintEventId();

  return useMutation({
    // First-touch attribution (utm_*, referrer) rides the register call so
    // the account is born attributed — no verified session needed.
    // SignupAttributionSync remains the catch-up for OAuth/invited paths.
    // The Cometly click token (register page has the pixel) rides along too;
    // the backend stores it last-write-wins, outside the write-once blob.
    mutationFn: async (data: RegisterRequest) => {
      const attribution = {
        ...(getStoredAttribution() ?? {}),
        comet_token: currentCometToken(),
      };
      const hasAttribution = Object.values(attribution).some(Boolean);
      return register({
        ...data,
        attribution: hasAttribution ? attribution : undefined,
        fingerprint_event_id: await getFingerprintEventId(),
      });
    },
    onSuccess: (data, variables) => {
      // Both AuthResponse and EmailVerificationRequiredResponse expose
      // pending_authentication_token. WorkOS-driven flows always require
      // email verification at this point, including the invited path
      // (see backend AuthService._register_via_invitation).
      const pendingAuthenticationToken = (
        data as { pending_authentication_token?: string | null }
      ).pending_authentication_token;

      showToast.success("Account created", {
        description: "Please check your email for a verification code.",
      });
      router.push(
        appendCurrentInvitationToken(
          buildVerifyEmailPath({
            email: variables.email,
            pendingAuthenticationToken,
          }),
        ),
      );
    },
    onError: (error: Error) => {
      showToast.error("Registration failed", {
        description: error.message || "Failed to create account.",
      });
    },
  });
}
